/**
 * Nutrition service layer.
 * Abstracts all Firestore operations for meal logging, nutrition goals,
 * and daily/weekly nutrition aggregation.
 * Data is stored as subcollections under each user document.
 */
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { handleServiceError } from '../utils/error-handler.util';

/**
 * Serialize a Firestore meal document snapshot into a plain object.
 * Converts Firestore Timestamps to JS Date ISO strings.
 * @param {import('firebase/firestore').DocumentSnapshot} docSnap
 * @returns {Object} A plain meal object.
 */
const serializeMeal = (docSnap) => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    title: data.title || '',
    mealType: data.mealType || 'snack',
    calories: data.calories || 0,
    macros: {
      protein: data.macros?.protein || 0,
      carbs: data.macros?.carbs || 0,
      fat: data.macros?.fat || 0,
      fiber: data.macros?.fiber || 0,
    },
    ingredients: data.ingredients || [],
    recipe: data.recipe || null,
    imageUrl: data.imageUrl || null,
    date: data.date instanceof Timestamp
      ? data.date.toDate().toISOString()
      : data.date || new Date().toISOString(),
    sharedAsPostId: data.sharedAsPostId || null,
    createdAt: data.createdAt instanceof Timestamp
      ? data.createdAt.toDate().toISOString()
      : data.createdAt || new Date().toISOString(),
    updatedAt: data.updatedAt instanceof Timestamp
      ? data.updatedAt.toDate().toISOString()
      : data.updatedAt || null,
  };
};

/**
 * Log a new meal to the user's meals subcollection.
 * @param {string} userId - The user's ID.
 * @param {Object} mealData - The meal data to save.
 * @returns {Promise<Object>} The created meal object with its Firestore ID.
 */
export const logMeal = async (userId, mealData) => {
  try {
    const mealsRef = collection(db, 'users', userId, 'meals');
    const mealDocRef = doc(mealsRef);
    const mealId = mealDocRef.id;

    const mealDate = mealData.date ? new Date(mealData.date) : new Date();
    mealDate.setHours(12, 0, 0, 0);

    const data = {
      title: mealData.title || 'Meal',
      mealType: mealData.mealType || 'snack',
      calories: mealData.calories || 0,
      macros: {
        protein: mealData.macros?.protein || 0,
        carbs: mealData.macros?.carbs || 0,
        fat: mealData.macros?.fat || 0,
        fiber: mealData.macros?.fiber || 0,
      },
      ingredients: mealData.ingredients || [],
      recipe: mealData.recipe || null,
      imageUrl: mealData.imageUrl || null,
      date: Timestamp.fromDate(mealDate),
      sharedAsPostId: mealData.sharedAsPostId || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(mealDocRef, data);

    return {
      id: mealId,
      ...data,
      date: mealDate.toISOString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  } catch (error) {
    handleServiceError(error, 'Log meal', 'Failed to save meal. Please try again.');
  }
};

/**
 * Get all meals for a specific day.
 * @param {string} userId - The user's ID.
 * @param {Date} date - The date to query for.
 * @returns {Promise<Array>} Array of meal objects.
 */
export const getMealsByDate = async (userId, date) => {
  try {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    const mealsRef = collection(db, 'users', userId, 'meals');
    const q = query(
      mealsRef,
      where('date', '>=', Timestamp.fromDate(startOfDay)),
      where('date', '<=', Timestamp.fromDate(endOfDay)),
      orderBy('date', 'asc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(serializeMeal);
  } catch (error) {
    handleServiceError(error, 'Get meals by date', 'Failed to load meals. Please try again.');
  }
};

/**
 * Get meals within a date range (e.g. for weekly view).
 * @param {string} userId - The user's ID.
 * @param {Date} startDate - Start date.
 * @param {Date} endDate - End date.
 * @returns {Promise<Array>} Array of meal objects.
 */
export const getMealsByDateRange = async (userId, startDate, endDate) => {
  try {
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    const mealsRef = collection(db, 'users', userId, 'meals');
    const q = query(
      mealsRef,
      where('date', '>=', Timestamp.fromDate(start)),
      where('date', '<=', Timestamp.fromDate(end)),
      orderBy('date', 'asc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(serializeMeal);
  } catch (error) {
    handleServiceError(error, 'Get meals by date range', 'Failed to load meals. Please try again.');
  }
};

/**
 * Get a single meal by ID.
 * @param {string} userId - The user's ID.
 * @param {string} mealId - The meal document ID.
 * @returns {Promise<Object|null>} The meal object or null.
 */
export const getMealById = async (userId, mealId) => {
  try {
    const docRef = doc(db, 'users', userId, 'meals', mealId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return serializeMeal(docSnap);
  } catch (error) {
    handleServiceError(error, 'Get meal', 'Failed to load meal. Please try again.');
  }
};

/**
 * Update an existing meal.
 * @param {string} userId - The user's ID.
 * @param {string} mealId - The meal document ID.
 * @param {Object} updates - Fields to update.
 * @returns {Promise<Object>} The updated meal object.
 */
export const updateMeal = async (userId, mealId, updates) => {
  try {
    const docRef = doc(db, 'users', userId, 'meals', mealId);
    const updateData = {
      ...updates,
      updatedAt: serverTimestamp(),
    };

    if (updates.date) {
      const mealDate = new Date(updates.date);
      mealDate.setHours(12, 0, 0, 0);
      updateData.date = Timestamp.fromDate(mealDate);
    }

    await updateDoc(docRef, updateData);

    const updatedSnap = await getDoc(docRef);
    return serializeMeal(updatedSnap);
  } catch (error) {
    handleServiceError(error, 'Update meal', 'Failed to update meal. Please try again.');
  }
};

/**
 * Delete a meal.
 * @param {string} userId - The user's ID.
 * @param {string} mealId - The meal document ID.
 * @returns {Promise<string>} The deleted meal ID.
 */
export const deleteMeal = async (userId, mealId) => {
  try {
    const docRef = doc(db, 'users', userId, 'meals', mealId);
    await deleteDoc(docRef);
    return mealId;
  } catch (error) {
    handleServiceError(error, 'Delete meal', 'Failed to delete meal. Please try again.');
  }
};

/**
 * Get aggregated daily totals for calories and macros.
 * @param {string} userId - The user's ID.
 * @param {Date} date - The date to aggregate for.
 * @returns {Promise<Object>} Totals object with calories, protein, carbs, fat, fiber.
 */
export const getDailyTotals = async (userId, date) => {
  try {
    const meals = await getMealsByDate(userId, date);
    const totals = {
      calories: 0,
      protein: 0,
      carbs: 0,
      fat: 0,
      fiber: 0,
      mealCount: meals.length,
    };

    meals.forEach((meal) => {
      totals.calories += meal.calories || 0;
      totals.protein += meal.macros?.protein || 0;
      totals.carbs += meal.macros?.carbs || 0;
      totals.fat += meal.macros?.fat || 0;
      totals.fiber += meal.macros?.fiber || 0;
    });

    return totals;
  } catch (error) {
    handleServiceError(error, 'Get daily totals', 'Failed to load nutrition totals.');
  }
};

/**
 * Set daily nutrition goals for a user.
 * Uses a single document with ID 'current' in the nutritionGoals subcollection.
 * @param {string} userId - The user's ID.
 * @param {Object} goals - The nutrition goals.
 * @param {number} goals.dailyCalories - Daily calorie target.
 * @param {Object} goals.macroTargets - Macro targets in grams.
 * @returns {Promise<Object>} The saved goals object.
 */
export const setNutritionGoals = async (userId, goals) => {
  try {
    const docRef = doc(db, 'users', userId, 'nutritionGoals', 'current');

    const data = {
      dailyCalories: goals.dailyCalories || 2200,
      macroTargets: {
        protein: goals.macroTargets?.protein || 150,
        carbs: goals.macroTargets?.carbs || 250,
        fat: goals.macroTargets?.fat || 70,
        fiber: goals.macroTargets?.fiber || 30,
      },
      updatedAt: serverTimestamp(),
    };

    await setDoc(docRef, data, { merge: true });

    return {
      ...data,
      updatedAt: new Date().toISOString(),
    };
  } catch (error) {
    handleServiceError(error, 'Set nutrition goals', 'Failed to save nutrition goals. Please try again.');
  }
};

/**
 * Get daily nutrition goals for a user.
 * @param {string} userId - The user's ID.
 * @returns {Promise<Object|null>} The goals object or null if not set.
 */
export const getNutritionGoals = async (userId) => {
  try {
    const docRef = doc(db, 'users', userId, 'nutritionGoals', 'current');
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    return {
      dailyCalories: data.dailyCalories || 2200,
      macroTargets: {
        protein: data.macroTargets?.protein || 150,
        carbs: data.macroTargets?.carbs || 250,
        fat: data.macroTargets?.fat || 70,
        fiber: data.macroTargets?.fiber || 30,
      },
      updatedAt: data.updatedAt instanceof Timestamp
        ? data.updatedAt.toDate().toISOString()
        : data.updatedAt || null,
    };
  } catch (error) {
    handleServiceError(error, 'Get nutrition goals', 'Failed to load nutrition goals.');
  }
};
